import { Link } from '@/shared/ui/Link/Link';
import { formatShortId } from '@/core/utils/idFormatter';
import type {
    ActiveOrderResponse,
    GetUserInfoResponse,
    LastOrderResponse,
} from '@/core/generated/api';
import { RequestErrorBanner, RequestLoading } from '@/shared/ui/RequestState';
import { RequestStatus } from '@/shared/model/requestStatus';
import type { ChatCompanion } from '@/entities/Chat/model/types';
import classes from '@/entities/Chat/ui/ChatWorkspace.module.scss';

interface CompanionDetailsProps {
    companion: ChatCompanion;
    userInfo: GetUserInfoResponse | null;
    status: RequestStatus;
    error: string | null;

    onRetry: () => void;
}

/** Показывает дату сведений о собеседнике */
const formatDate = (
    value: string | null | undefined,
    options: Intl.DateTimeFormatOptions,
): string => {
    if (!value) return '—';

    const date = new Date(value);

    return Number.isNaN(date.getTime())
        ? '—'
        : date.toLocaleString('ru-RU', options);
};

/** Возвращает ссылку на профиль собеседника */
const getUserLink = (
    companion: ChatCompanion,
    userInfo: GetUserInfoResponse,
): { href: string; label: string; fullId: string } | null => {
    if (companion.role === 'Client' && userInfo.clientId) {
        return {
            href: `/clients/${userInfo.clientId}`,
            label: formatShortId(userInfo.clientId),
            fullId: userInfo.clientId,
        };
    }

    if (companion.role === 'Executor' && userInfo.executorId) {
        return {
            href: `/executors/${userInfo.executorId}`,
            label: formatShortId(userInfo.executorId),
            fullId: userInfo.executorId,
        };
    }

    return null;
};

/** Отображает карточку активного заказа собеседника */
const ActiveOrderCard = ({ order }: { order: ActiveOrderResponse }) => {
    const counterpart = order.executorName
        ? {
            title: 'Исполнитель',
            name: order.executorName,
            phone: order.executorPhone,
            id: order.executorId,
            path: 'executors',
            copyLabel: 'Копировать ID исполнителя',
        }
        : order.clientName
            ? {
                title: 'Клиент',
                name: order.clientName,
                phone: order.clientPhone,
                id: order.clientId,
                path: 'clients',
                copyLabel: 'Копировать ID клиента',
            }
            : null;

    return (
        <article className={classes.orderCard}>
            <div className={classes.orderCardHeader}>
                {order.orderId
                    ? (
                        <Link
                            url={`/orders/${order.orderId}`}
                            text={order.orderNumber ?? 'Заказ'}
                            isBlank={false}
                        />
                    )
                    : (
                        <strong>{order.orderNumber ?? 'Заказ'}</strong>
                    )}
                <span className={classes.orderStatus}>{order.status ?? 'В работе'}</span>
            </div>
            {order.orderType &&
                <div className={classes.orderType}>🏷️ {order.orderType}</div>
            }
            <p>📅 {formatDate(order.orderDate, {
                dateStyle: 'short',
                timeStyle: 'short'
            })}</p>
            {counterpart
                ? (
                    <div className={classes.orderCounterpart}>
                        <strong>{counterpart.title}:</strong> {counterpart.name}
                        <span>
                            📞 {counterpart.phone ?? '—'}
                            {counterpart.id && (
                                <>
                                    {' · '}
                                    <Link
                                        url={`/${counterpart.path}/${counterpart.id}`}
                                        text={`ID: ${formatShortId(counterpart.id)}`}
                                        isBlank={false}
                                        showCopyButton
                                        copyText={counterpart.id}
                                        copyLabel={counterpart.copyLabel}
                                    />
                                </>
                            )}
                        </span>
                    </div>
                )
                : (
                    <p className={classes.mutedText}>Исполнитель не назначен</p>
                )}
        </article>
    );
};

/** Отображает карточку последнего завершённого заказа */
const LastOrderCard = ({ order }: { order: LastOrderResponse | undefined }) => {
    if (!order) {
        return <p className={classes.emptyCard}>Нет данных о последнем заказе</p>;
    }

    return (
        <article className={`${classes.orderCard} ${classes.lastOrderCard}`}>
            {order.orderId
                ? (
                    <Link
                        url={`/orders/${order.orderId}`}
                        text={order.orderNumber ?? 'Заказ'}
                        isBlank={false}
                    />
                )
                : (
                    <strong>{order.orderNumber ?? 'Заказ'}</strong>
                )}
            <span className={classes.lastOrderBadge}>последний</span>
            <p>📅 {formatDate(order.orderDate, {
                dateStyle: 'short',
                timeStyle: 'short'
            })}</p>
            <p className={classes.mutedText}>Заказ завершён</p>
        </article>
    );
};

/** Отображает сведения о собеседнике, заказах и питомцах */
export const CompanionDetails = (
    {
        companion,
        userInfo,
        status,
        error,
        onRetry,
    }: CompanionDetailsProps
) => {
    const name = userInfo?.name || companion.name || 'Неизвестно';
    const photoUrl = userInfo?.photoUrl || companion.photoUrl;

    const userLink = userInfo
        ? getUserLink(companion, userInfo)
        : null;

    return (
        <aside
            className={classes.detailsPanel}
            aria-label="Информация о пользователе"
        >
            <div className={classes.detailsBody}>
                <section className={classes.profileSummary}>
                    <div
                        className={classes.profileAvatar}
                        aria-hidden="true"
                    >
                        {photoUrl
                            ? <img
                                src={photoUrl}
                                alt=""
                            />
                            : name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <h3>{name}</h3>
                        <p><strong>Тип:</strong> {companion.displayNameRole}</p>
                        <p>
                            <strong>ID:</strong>{' '}
                            {userLink
                                ? (
                                    <Link
                                        url={userLink.href}
                                        text={userLink.label}
                                        isBlank={false}
                                        showCopyButton
                                        copyText={userLink.fullId}
                                        copyLabel="Копировать ID профиля"
                                    />
                                )
                                : '—'}
                        </p>
                    </div>
                </section>

                {status === RequestStatus.Loading && (
                    <RequestLoading message="Загружаем информацию..." />
                )}
                <RequestErrorBanner
                    status={status}
                    error={error}
                    fallbackMessage="Не удалось загрузить информацию"
                    onRetry={onRetry}
                />
                {status === RequestStatus.Success && userInfo && (
                    <>
                        <section className={classes.detailsSection}>
                            <h3>Контактная информация</h3>
                            <dl className={classes.detailsList}>
                                <div>
                                    <dt>Дата регистрации</dt>
                                    <dd>{formatDate(userInfo.registrationDate, {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric',
                                    })}</dd>
                                </div>
                                <div>
                                    <dt>Телефон</dt>
                                    <dd>{userInfo.phone ?? '—'}</dd>
                                </div>
                            </dl>
                        </section>

                        {companion.role === 'Client' && (
                            <section className={classes.detailsSection}>
                                <h3>Питомцы</h3>
                                {userInfo.pets?.length
                                    ? (
                                        <ul className={classes.petList}>
                                            {userInfo.pets.map((pet, index) => {
                                                const icon = pet.petType?.id === 'cat'
                                                    ? '🐱'
                                                    : pet.petType?.id === 'dog'
                                                        ? '🐶'
                                                        : '🐾';

                                                return (
                                                    <li key={pet.petId ?? `${pet.name ?? 'pet'}-${index}`}>
                                                        <span>
                                                            {pet.name ?? 'Без имени'} ({icon} {pet.petType?.name ?? 'без типа'})
                                                        </span>
                                                        {pet.petId && (
                                                            <Link
                                                                url={`/pets/${pet.petId}`}
                                                                text="Открыть"
                                                                isBlank={false}
                                                            />
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )
                                    : (
                                        <p className={classes.mutedText}>Нет данных</p>
                                    )}
                            </section>
                        )}

                        <section className={classes.detailsSection}>
                            <h3>Активные заказы</h3>
                            {userInfo.activeOrders?.length
                                ? (
                                    <div className={classes.orderList}>
                                        {userInfo.activeOrders.map((order, index) => (
                                            <ActiveOrderCard
                                                key={order.orderId ?? `${order.orderNumber ?? 'order'}-${index}`}
                                                order={order}
                                            />
                                        ))}
                                    </div>
                                )
                                : (
                                    <p className={classes.emptyCard}>Нет активных заказов</p>
                                )}
                        </section>

                        <section className={classes.detailsSection}>
                            <h3>Последний заказ</h3>
                            <LastOrderCard order={userInfo.lastOrder} />
                        </section>
                    </>
                )}

            </div>
        </aside>
    );
};
